type AdminNameHistory = {
    accountId: number,
    displayName: string,
    tripcode: string,
    firstSeenAt: string,
    lastSeenAt: string,
    seenCount: number,
};

type AdminAccount = {
    id: number,
    isEmailVerified: boolean,
    streamPermission: string,
    isBanned: boolean,
    banReason: string,
    role: string,
    createdAt: string,
    updatedAt: string,
    lastDisplayName: string,
    lastIpAddress: string,
    lastLoginAt: string | null,
    loginCount: number,
    nameHistory: AdminNameHistory[],
};

type AdminChatLog = {
    id: number,
    accountId: number | null,
    displayName: string,
    areaId: string,
    roomId: string,
    ipAddress: string,
    messageBody: string,
    createdAt: string,
    isBanned: boolean,
    role: string,
};


const ADMIN_BUTTON_ID = "noah-admin-button";
const ADMIN_PANEL_ID = "noah-admin-panel";
const ADMIN_STYLE_ID = "noah-admin-style";
const ADMIN_SEARCH_FORM_ID = "noah-admin-search-form";
const ADMIN_SEARCH_INPUT_ID = "noah-admin-search-input";
const ADMIN_MESSAGE_ID = "noah-admin-message";
const ADMIN_BODY_ID = "noah-admin-body";

let adminAccounts: AdminAccount[] = [];
let adminChatLogs: AdminChatLog[] = [];
let adminSearchQuery = "";
let isAdminLoading = false;
let adminMessage = "";

function escapeHtml(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, character => {
        switch (character) {
            case "&": return "&amp;";
            case "<": return "&lt;";
            case ">": return "&gt;";
            case '"': return "&quot;";
            case "'": return "&#039;";
            default: return character;
        }
    });
}

function formatDateTime(value: unknown): string {
    if (!value) {
        return "-";
    }

    const date = new Date(String(value));

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

async function isCurrentUserAdmin(): Promise<boolean> {
    try {
        const response = await fetch("/api/auth/me", {
            credentials: "same-origin",
        });

        const data = await response.json();

        if (!response.ok || !data.ok || !data.isLoggedIn || !data.account) {
            return false;
        }

        return !data.account.isBanned && (
            data.account.role === "admin" ||
            data.account.role === "moderator"
        );
    }
    catch {
        return false;
    }
}

function applyAdminStyle(): void {
    if (document.getElementById(ADMIN_STYLE_ID)) {
        return;
    }

    const style = document.createElement("style");
    style.id = ADMIN_STYLE_ID;
    style.textContent = `
#${ADMIN_BUTTON_ID} {
    position: fixed;
    right: 12px;
    bottom: 62px;
    z-index: 99999;
    padding: 7px 10px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.72);
    color: #fff;
    font-size: 12px;
    cursor: pointer;
}

#${ADMIN_PANEL_ID} {
    position: fixed;
    left: 50%;
    top: 50%;
    z-index: 100000;
    display: none;
    width: min(1100px, calc(100vw - 24px));
    max-height: calc(100vh - 40px);
    transform: translate(-50%, -50%);
    padding: 14px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 10px;
    background: rgba(20, 20, 20, 0.96);
    color: #fff;
    box-sizing: border-box;
    overflow: auto;
    font-size: 13px;
}

#${ADMIN_PANEL_ID}.is-open {
    display: block;
}

.noah-admin-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
}

.noah-admin-header h2 {
    margin: 0;
    font-size: 18px;
}

#${ADMIN_SEARCH_FORM_ID} {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

#${ADMIN_SEARCH_INPUT_ID} {
    min-width: 240px;
    flex: 1;
}

#${ADMIN_PANEL_ID} button {
    cursor: pointer;
}

#${ADMIN_MESSAGE_ID} {
    margin-bottom: 10px;
    min-height: 1.4em;
}

.noah-admin-account {
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 8px;
    padding: 10px;
    margin-bottom: 10px;
    background: rgba(255, 255, 255, 0.06);
}

.noah-admin-account.is-banned {
    border-color: rgba(255, 90, 90, 0.75);
    background: rgba(120, 0, 0, 0.25);
}

.noah-admin-account-title {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 8px;
}

.noah-admin-account-name {
    font-weight: bold;
    font-size: 15px;
}

.noah-admin-grid {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 4px 10px;
    margin-bottom: 8px;
}

.noah-admin-label {
    opacity: 0.75;
}

.noah-admin-history {
    margin-top: 8px;
    padding-left: 18px;
}

.noah-admin-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 8px;
}

.noah-admin-section-title {
    margin: 14px 0 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
    font-weight: bold;
    font-size: 15px;
}

.noah-admin-chat-log {
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 8px;
    padding: 8px;
    margin-bottom: 8px;
    background: rgba(255, 255, 255, 0.05);
}

.noah-admin-chat-log.is-banned {
    border-color: rgba(255, 90, 90, 0.75);
    background: rgba(120, 0, 0, 0.25);
}

.noah-admin-chat-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 5px;
    opacity: 0.82;
}

.noah-admin-chat-body {
    white-space: pre-wrap;
    word-break: break-word;
    margin-bottom: 6px;
    padding: 6px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.25);
}


.noah-admin-danger {
    color: #fff;
    background: #8b1d1d;
    border: 1px solid #d66;
    border-radius: 4px;
    padding: 4px 8px;
}

.noah-admin-normal {
    border-radius: 4px;
    padding: 4px 8px;
}

@media screen and (max-width: 700px) {
    #${ADMIN_BUTTON_ID} {
        right: 6px;
        bottom: 94px;
    }

    .noah-admin-grid {
        grid-template-columns: 1fr;
    }
}
`;

    document.head.appendChild(style);
}


function renderChatLogsHtml(): string {
    if (adminChatLogs.length === 0) {
        return `<div>直近発言ログはありません。</div>`;
    }

    return adminChatLogs.map(log => {
        const accountText = log.accountId === null ? "未紐付け" : `ID ${log.accountId}`;
        const banButton = log.accountId === null
            ? ""
            : log.isBanned
                ? `<button class="noah-admin-normal" type="button" data-action="unban" data-account-id="${escapeHtml(log.accountId)}">このアカウントのBAN解除</button>`
                : `<button class="noah-admin-danger" type="button" data-action="ban" data-account-id="${escapeHtml(log.accountId)}">このアカウントをBAN</button>`;

        return `
            <div class="noah-admin-chat-log ${log.isBanned ? "is-banned" : ""}">
                <div class="noah-admin-chat-meta">
                    <span>${escapeHtml(formatDateTime(log.createdAt))}</span>
                    <span>${escapeHtml(accountText)}</span>
                    <span>${escapeHtml(log.displayName || "HN未取得")}</span>
                    <span>${escapeHtml(log.areaId || "-")} / ${escapeHtml(log.roomId || "-")}</span>
                    <span>${escapeHtml(log.ipAddress || "-")}</span>
                    <span>${escapeHtml(log.isBanned ? "BAN中" : "通常")}</span>
                </div>
                <div class="noah-admin-chat-body">${escapeHtml(log.messageBody)}</div>
                <div class="noah-admin-actions">
                    ${banButton}
                </div>
            </div>
        `;
    }).join("");
}


function renderAdminPanel(): void {
    const panel = document.getElementById(ADMIN_PANEL_ID);

    if (!panel) {
        return;
    }

    const accountsHtml = adminAccounts.length === 0
        ? `<div>対象アカウントはありません。</div>`
        : adminAccounts.map(account => {
            const historyHtml = account.nameHistory.length === 0
                ? `<li>HN履歴なし</li>`
                : account.nameHistory.slice(0, 8).map(history => `
                    <li>
                        ${escapeHtml(history.displayName)}
                        ${history.tripcode ? ` / ${escapeHtml(history.tripcode)}` : ""}
                        <span>(${escapeHtml(history.seenCount)}回 / 最終: ${escapeHtml(formatDateTime(history.lastSeenAt))})</span>
                    </li>
                `).join("");

            const statusText = account.isBanned ? "BAN中" : "通常";
            const banReason = account.banReason ? account.banReason : "-";

            return `
                <div class="noah-admin-account ${account.isBanned ? "is-banned" : ""}" data-account-id="${escapeHtml(account.id)}">
                    <div class="noah-admin-account-title">
                        <div class="noah-admin-account-name">
                            ID ${escapeHtml(account.id)} / ${escapeHtml(account.lastDisplayName || "HN未取得")}
                        </div>
                        <div>${escapeHtml(statusText)} / ${escapeHtml(account.role)}</div>
                    </div>

                    <div class="noah-admin-grid">
                        <div class="noah-admin-label">メール認証</div>
                        <div>${account.isEmailVerified ? "済み" : "未認証"}</div>

                        <div class="noah-admin-label">配信権限</div>
                        <div>${escapeHtml(account.streamPermission)}</div>

                        <div class="noah-admin-label">最終ログイン</div>
                        <div>${escapeHtml(formatDateTime(account.lastLoginAt))}</div>

                        <div class="noah-admin-label">最終IP</div>
                        <div>${escapeHtml(account.lastIpAddress || "-")}</div>

                        <div class="noah-admin-label">ログイン回数</div>
                        <div>${escapeHtml(account.loginCount)}</div>

                        <div class="noah-admin-label">作成日時</div>
                        <div>${escapeHtml(formatDateTime(account.createdAt))}</div>

                        <div class="noah-admin-label">BAN理由</div>
                        <div>${escapeHtml(banReason)}</div>
                    </div>

                    <details>
                        <summary>HN履歴</summary>
                        <ul class="noah-admin-history">
                            ${historyHtml}
                        </ul>
                    </details>

                    <div class="noah-admin-actions">
                        ${
                            account.isBanned
                                ? `<button class="noah-admin-normal" type="button" data-action="unban" data-account-id="${escapeHtml(account.id)}">BAN解除</button>`
                                : `<button class="noah-admin-danger" type="button" data-action="ban" data-account-id="${escapeHtml(account.id)}">BAN</button>`
                        }
                    </div>
                </div>
            `;
        }).join("");

    panel.innerHTML = `
        <div class="noah-admin-header">
            <h2>管理者画面</h2>
            <button type="button" data-action="close">閉じる</button>
        </div>

        <form id="${ADMIN_SEARCH_FORM_ID}">
            <input
                id="${ADMIN_SEARCH_INPUT_ID}"
                type="search"
                placeholder="ID / HN / トリップで検索"
                value="${escapeHtml(adminSearchQuery)}"
            />
            <button type="submit">検索</button>
            <button type="button" data-action="reload">再読み込み</button>
        </form>

        <div id="${ADMIN_MESSAGE_ID}">
            ${escapeHtml(isAdminLoading ? "読み込み中..." : adminMessage)}
        </div>

        <div class="noah-admin-section-title">アカウント一覧</div>
        <div id="${ADMIN_BODY_ID}">
            ${accountsHtml}
        </div>

        <div class="noah-admin-section-title">直近発言ログ</div>
        <div>
            ${renderChatLogsHtml()}
        </div>
    `;
}

async function loadAdminAccounts(): Promise<void> {
    isAdminLoading = true;
    adminMessage = "";
    renderAdminPanel();

    try {
        const params = new URLSearchParams();
        params.set("limit", "100");

        if (adminSearchQuery.trim() !== "") {
            params.set("q", adminSearchQuery.trim());
        }

        const response = await fetch(`/api/admin/accounts?${params.toString()}`, {
            credentials: "same-origin",
        });

        const data = await response.json();

        if (!response.ok || !data.ok) {
            throw new Error(data.error || "load_failed");
        }

        adminAccounts = Array.isArray(data.accounts) ? data.accounts : [];

        const logParams = new URLSearchParams();
        logParams.set("limit", "50");

        if (adminSearchQuery.trim() !== "") {
            logParams.set("q", adminSearchQuery.trim());
        }

        const logResponse = await fetch(`/api/admin/chat-logs?${logParams.toString()}`, {
            credentials: "same-origin",
        });

        const logData = await logResponse.json();

        if (!logResponse.ok || !logData.ok) {
            throw new Error(logData.error || "chat_logs_load_failed");
        }

        adminChatLogs = Array.isArray(logData.logs) ? logData.logs : [];
        adminMessage = `${adminAccounts.length}件表示中 / 直近発言ログ ${adminChatLogs.length}件`;
    }
    catch (e) {
        adminAccounts = [];
        adminChatLogs = [];
        adminMessage = `読み込みに失敗しました: ${e instanceof Error ? e.message : String(e)}`;
    }
    finally {
        isAdminLoading = false;
        renderAdminPanel();
    }
}

async function postAdminAction(accountId: number, action: "ban" | "unban", reason = ""): Promise<void> {
    const response = await fetch(`/api/admin/accounts/${accountId}/${action}`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason }),
    });

    const data = await response.json();

    if (!response.ok || !data.ok) {
        throw new Error(data.error || "request_failed");
    }
}

function createAdminPanel(): void {
    if (document.getElementById(ADMIN_BUTTON_ID)) {
        return;
    }

    applyAdminStyle();

    const button = document.createElement("button");
    button.id = ADMIN_BUTTON_ID;
    button.type = "button";
    button.textContent = "管理";

    const panel = document.createElement("div");
    panel.id = ADMIN_PANEL_ID;

    document.body.appendChild(button);
    document.body.appendChild(panel);

    button.addEventListener("click", () => {
        panel.classList.add("is-open");

        if (adminAccounts.length === 0) {
            loadAdminAccounts();
        }
        else {
            renderAdminPanel();
        }
    });

    panel.addEventListener("submit", event => {
        const form = event.target as HTMLFormElement | null;

        if (!form || form.id !== ADMIN_SEARCH_FORM_ID) {
            return;
        }

        event.preventDefault();

        const input = document.getElementById(ADMIN_SEARCH_INPUT_ID) as HTMLInputElement | null;
        adminSearchQuery = input ? input.value.trim() : "";

        loadAdminAccounts();
    });

    panel.addEventListener("click", async event => {
        const target = event.target as HTMLElement | null;
        const actionElement = target?.closest("[data-action]") as HTMLElement | null;

        if (!actionElement) {
            return;
        }

        const action = actionElement.dataset.action;

        if (action === "close") {
            panel.classList.remove("is-open");
            return;
        }

        if (action === "reload") {
            loadAdminAccounts();
            return;
        }

        const accountId = Number(actionElement.dataset.accountId);

        if (!Number.isInteger(accountId) || accountId <= 0) {
            return;
        }

        try {
            if (action === "ban") {
                const reason = window.prompt(`アカウントID ${accountId} をBANします。理由を入力してください。`, "") || "";

                if (!window.confirm(`アカウントID ${accountId} をBANします。よろしいですか？`)) {
                    return;
                }

                await postAdminAction(accountId, "ban", reason);
                adminMessage = `アカウントID ${accountId} をBANしました。`;
                await loadAdminAccounts();
                return;
            }

            if (action === "unban") {
                if (!window.confirm(`アカウントID ${accountId} のBANを解除します。よろしいですか？`)) {
                    return;
                }

                await postAdminAction(accountId, "unban");
                adminMessage = `アカウントID ${accountId} のBANを解除しました。`;
                await loadAdminAccounts();
                return;
            }
        }
        catch (e) {
            adminMessage = `操作に失敗しました: ${e instanceof Error ? e.message : String(e)}`;
            renderAdminPanel();
        }
    });

    renderAdminPanel();
}

export async function initAdminPanel(): Promise<void> {
    const isAdmin = await isCurrentUserAdmin();

    if (!isAdmin) {
        return;
    }

    createAdminPanel();
}
